import React, { useState, useReducer } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Image,
  Form,
  Button,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useForm } from "react-hook-form";

import { authenticate } from "../../services/accountService";
import { useAuthContext } from "../../context/authProvider";
import { notify, initialState } from "../../store/notification";
import { SITEURL } from "../../constants";

import logo from "../../resources/images/logo.svg";

/**
 * Component for user login.
 * @returns {React.Element} - Returns JSX for user login.
 * @access Accessible by SuperAdmin, Staff, and Validator.
 */
const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();

  // Notification state and dispatch hook
  const [notification, dispatch] = useReducer(notify, initialState);
  const [isLoading, setIsLoading] = useState(false);
  // Destructure the 'saveToken' and 'returnURL' object from the 'useAuthContext()' hook
  const { saveToken, returnURL } = useAuthContext();

  /**
   * Function to handle user login.
   * @param {Object} data - The data containing user login credentials.
   */
  const handleLogin = async (data) => {
    setIsLoading(true);
    try {
      const response = await authenticate(data);
      setIsLoading(false);
      if (response.isRequiresTwoFactor) {
        // If two-factor authentication is required, navigate to verify account page
        navigate(
          returnURL
            ? `/verify-account?returnUrl=${returnURL}`
            : "/verify-account",
          {
            replace: true,
            state: { email: data.email },
          }
        );
      } else if (response.isForceToChangePwd)
        // If user is forced to change password, navigate to password change request page
        navigate(
          returnURL
            ? `/password-change-request?returnUrl=${returnURL}`
            : "/password-change-request"
        );
      else {
        // Persist the JWT token in the client's browser using local storage.
        // The second parameter indicates whether it is from the login page.
        saveToken(response.token, true);
      }
    } catch (error) {
      // Show error notification if authentication fails
      showNotification("danger", error, 5000);
      setIsLoading(false);
    }
  };

  /**
   * Function to dispatch an action to show a notification.
   * @param {string} variant - The variant of the notification (e.g., 'success', 'error', 'info').
   * @param {string} message - The message content of the notification.
   * @param {number} timeout - The duration in milliseconds before the notification auto-dismisses (optional).
   */
  const showNotification = (variant, message, timeout) => {
    dispatch({
      type: "SHOW_NOTIFICATION",
      payload: {
        variant: variant,
        message: message,
        timeout: timeout,
      },
      dispatch: dispatch,
    });
  };

  return (
    <main className="d-flex align-items-center justify-content-center min-vh-100 flex-column">
      <Container>
        <Row className="justify-content-center">
          <Col xs={11} sm={10} md={8} lg={6} xl={5} xxl={4}>
            <div className="text-center mb-5">
              <Link to={SITEURL} title="Benevolent">
                <Image src={logo} alt="Benevolent" className="logo" fluid />
              </Link>
            </div>
            <Card className="border-0 shadow p-4 rounded-4">
              <Card.Body>
                <Form onSubmit={handleSubmit(handleLogin)}>
                  <Form.Group className="mb-4">
                    <InputGroup>
                      <Form.Floating>
                        <Form.Control
                          type="email"
                          placeholder="Email"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                        />
                        <Form.Label>Email</Form.Label>
                      </Form.Floating>
                      <InputGroup.Text>
                        <i className="flaticon-mail fs-4"></i>
                      </InputGroup.Text>
                    </InputGroup>
                    {errors.email && (
                      <Form.Text className="text-danger">
                        {errors.email.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <InputGroup>
                      <Form.Floating>
                        <Form.Control
                          type="password"
                          placeholder="Password"
                          {...register("password", {
                            required: "Password is required",
                          })}
                        />
                        <Form.Label>Password</Form.Label>
                      </Form.Floating>
                      <InputGroup.Text>
                        <i className="flaticon-password fs-3"></i>
                      </InputGroup.Text>
                    </InputGroup>
                    {errors.password && (
                      <Form.Text className="text-danger">
                        {errors.password.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                  <Row className="mb-5">
                    {/* <Col>
                      <Form.Check
                        type="checkbox"
                        label="Remember Me"
                        className="fw-light"
                      />
                    </Col> */}
                    <Col className="text-end">
                      <Link
                        to="/recovery-password"
                        title="Recovery Password"
                        className="text-decoration-none link-secondary fw-light"
                      >
                        Recovery Password?
                      </Link>
                    </Col>
                  </Row>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      disabled={isLoading}
                      type="submit"
                      size="lg"
                    >
                      {isLoading ? (
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "SIGN IN"
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
            <div className="text-center mt-5">
              <p className="mb-0 text-dark-emphasis">
                Not a Member Yet?{" "}
                <Link
                  to="/register"
                  title="Register Now"
                  className="text-decoration-none link-secondary fw-semibold"
                >
                  Register Now
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      <div className="notification show migration-alert text-center">
        <Alert variant="dark" dismissible>
          For existing system users, kindly utilize the{" "}
          <Link
            to="/recovery-password"
            title="Recovery Password"
            className="text-decoration-none link-secondary fw-bold"
          >
            password recovery
          </Link>{" "}
          feature to initiate the process of resetting your password on the new
          system.
        </Alert>
      </div>

      <div className={`notification ${notification.variant && "show"}`}>
        {notification.variant && (
          <Alert
            variant={notification.variant}
            onClose={() => dispatch({ type: "CLEAR_NOTIFICATION" })}
            dismissible
          >
            {notification.message}
          </Alert>
        )}
      </div>
    </main>
  );
};

export default Login;
